import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

/**
 * Suivi des vidéos lancées, mis à disposition des composants média.
 *
 * Le contexte vaut `null` partout sauf sur un site publié dont le lecteur a
 * accepté le suivi de lecture : l'éditeur, `/preview/` et `/review/` n'ont
 * donc rien à savoir de ce module, et n'y branchent rien.
 *
 * Les composants média ne connaissent pas le bloc qui les contient — un
 * `VideoFrame` vit aussi bien dans un carrousel que dans une cellule de
 * tableau. Plutôt que de faire descendre un identifiant à travers cinq blocs,
 * on le **retrouve dans le DOM** : tous les blocs posent déjà leur id Puck sur
 * leur élément racine. C'est la même mécanique que l'ancrage des commentaires.
 */
export const VideoTrackingContext = createContext(null);

/**
 * @returns {{ rootRef, active: boolean, onPlay, onPause, onTime }}
 *   `rootRef` se pose sur l'élément racine du lecteur : c'est de là que part la
 *   remontée vers le bloc. La résolution est faite **une fois**, à la première
 *   remontée, et non à chaque `timeupdate`.
 */
export function useVideoReport() {
  const tracking = useContext(VideoTrackingContext);
  const rootRef = useRef(null);
  const idRef = useRef(undefined);

  // Changement de page : l'identifiant retrouvé ne vaut plus.
  useEffect(() => { idRef.current = undefined; }, [tracking]);

  const videoId = useCallback(() => {
    if (!tracking) return null;
    if (idRef.current === undefined) idRef.current = tracking.resolveId(rootRef.current);
    return idRef.current;
  }, [tracking]);

  return useMemo(() => ({
    rootRef,
    active: !!tracking,
    onPlay: () => tracking?.play(videoId()),
    onPause: () => tracking?.pause(videoId()),
    onTime: (currentSeconds, durationSeconds) => (
      tracking?.progress(videoId(), currentSeconds, durationSeconds)
    ),
  }), [tracking, videoId]);
}

export default VideoTrackingContext;
